import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Smartphone, Monitor, Moon, Sun, Download, Copy, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { listEmailTemplates, renderEmailTemplate } from "@/lib/email-preview.functions";

export const Route = createFileRoute("/_authenticated/dashboard/admin/email-preview")({
  head: () => ({ meta: [{ title: "Email Preview — Admin" }] }),
  beforeLoad: async ({ context }) => {
    const user = (context as { user?: { id: string } | null }).user;
    if (!user) throw redirect({ to: "/auth", search: { mode: "signin" } });
    const { supabase } = await import("@/integrations/supabase/client");
    const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
    if (!data) throw redirect({ to: "/dashboard" });
  },
  component: EmailPreviewPage,
});

type Device = "desktop" | "mobile";
type Mode = "light" | "dark";

function EmailPreviewPage() {
  const listFn = useServerFn(listEmailTemplates);
  const renderFn = useServerFn(renderEmailTemplate);

  const listQ = useQuery({
    queryKey: ["email-templates-list"],
    queryFn: () => listFn(),
    staleTime: 60_000,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>("desktop");
  const [mode, setMode] = useState<Mode>("light");
  const [dataDraft, setDataDraft] = useState<string>("");

  // Auto-select first template once loaded
  if (!selected && listQ.data && listQ.data.length > 0) {
    const first = listQ.data[0];
    setSelected(first.name);
    setDataDraft(first.previewDataJson);
  }

  const current = useMemo(
    () => listQ.data?.find((t) => t.name === selected) ?? null,
    [listQ.data, selected],
  );

  const renderQ = useQuery({
    queryKey: ["email-render", selected, dataDraft],
    enabled: !!selected,
    queryFn: async () => {
      let parsed: Record<string, unknown> | undefined;
      try {
        parsed = dataDraft ? JSON.parse(dataDraft) : undefined;
      } catch {
        throw new Error("JSON dati non valido");
      }
      return renderFn({ data: { name: selected!, data: parsed } });
    },
    retry: false,
    staleTime: 0,
  });

  function selectTemplate(name: string) {
    setSelected(name);
    const tpl = listQ.data?.find((t) => t.name === name);
    if (tpl) setDataDraft(tpl.previewDataJson);
  }

  async function copyHtml() {
    if (!renderQ.data?.html) return;
    await navigator.clipboard.writeText(renderQ.data.html);
    toast.success("HTML copiato negli appunti");
  }

  function downloadHtml() {
    if (!renderQ.data) return;
    const blob = new Blob([renderQ.data.html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${renderQ.data.name}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function resetData() {
    if (!current) return;
    setDataDraft(current.previewDataJson);
    toast.success("Dati ripristinati");
  }

  // Wrap rendered email in srcDoc with optional dark backdrop.
  const srcDoc = useMemo(() => {
    if (!renderQ.data?.html) return "";
    const bg = mode === "dark" ? "#0f0f0f" : "#f3f1ec";
    return `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head><body style="margin:0;padding:24px;background:${bg};">${renderQ.data.html}</body></html>`;
  }, [renderQ.data, mode]);

  return (
    <div className="container mx-auto max-w-7xl px-4 py-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link
            to="/dashboard/admin"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Torna all'Admin
          </Link>
          <h1 className="mt-2 font-display text-3xl inline-flex items-center gap-2">
            <Mail className="h-7 w-7 text-gold" /> Email Preview
          </h1>
          <p className="text-sm text-muted-foreground">
            QA visiva di tutti i template prima dell'invio reale. Nessuna email viene inviata.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar template list */}
        <aside className="col-span-12 lg:col-span-3">
          <div className="rounded-lg border bg-card p-2">
            <p className="px-2 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Template ({listQ.data?.length ?? 0})
            </p>
            <ul className="mt-1 space-y-1">
              {(listQ.data ?? []).map((t) => {
                const active = t.name === selected;
                return (
                  <li key={t.name}>
                    <button
                      type="button"
                      onClick={() => selectTemplate(t.name)}
                      className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                        active
                          ? "bg-gold/10 text-foreground ring-1 ring-gold/30"
                          : "hover:bg-muted"
                      }`}
                    >
                      <div className="font-medium">{t.displayName}</div>
                      <div className="truncate text-xs text-muted-foreground">{t.name}</div>
                    </button>
                  </li>
                );
              })}
              {listQ.isLoading && (
                <li className="px-3 py-4 text-center text-sm text-muted-foreground">Caricamento…</li>
              )}
            </ul>
          </div>
        </aside>

        {/* Main preview */}
        <main className="col-span-12 lg:col-span-9 space-y-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3">
            <div className="flex items-center gap-3">
              <Badge variant="secondary">{current?.name ?? "—"}</Badge>
              <span className="text-sm text-muted-foreground truncate">
                <strong className="text-foreground">Subject:</strong> {renderQ.data?.subject ?? current?.subject ?? "—"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex rounded-md border">
                <Button
                  size="sm"
                  variant={device === "desktop" ? "default" : "ghost"}
                  onClick={() => setDevice("desktop")}
                  className="rounded-r-none"
                >
                  <Monitor className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant={device === "mobile" ? "default" : "ghost"}
                  onClick={() => setDevice("mobile")}
                  className="rounded-l-none"
                >
                  <Smartphone className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex rounded-md border">
                <Button
                  size="sm"
                  variant={mode === "light" ? "default" : "ghost"}
                  onClick={() => setMode("light")}
                  className="rounded-r-none"
                >
                  <Sun className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant={mode === "dark" ? "default" : "ghost"}
                  onClick={() => setMode("dark")}
                  className="rounded-l-none"
                >
                  <Moon className="h-4 w-4" />
                </Button>
              </div>
              <Button size="sm" variant="outline" onClick={copyHtml} disabled={!renderQ.data}>
                <Copy className="h-4 w-4 mr-1" /> HTML
              </Button>
              <Button size="sm" variant="outline" onClick={downloadHtml} disabled={!renderQ.data}>
                <Download className="h-4 w-4 mr-1" /> .html
              </Button>
            </div>
          </div>

          {/* Iframe preview */}
          <div className="rounded-lg border bg-muted/40 p-4">
            <div className="mx-auto overflow-hidden rounded-md border bg-white shadow-sm"
              style={{ width: device === "mobile" ? 380 : "100%", maxWidth: "100%" }}>
              {renderQ.isLoading && (
                <div className="p-10 text-center text-sm text-muted-foreground">Rendering…</div>
              )}
              {renderQ.error && (
                <div className="p-6 text-sm text-destructive">
                  Errore: {(renderQ.error as Error).message}
                </div>
              )}
              {renderQ.data && (
                <iframe
                  title="email-preview"
                  srcDoc={srcDoc}
                  className="w-full"
                  style={{ height: device === "mobile" ? 720 : 900, border: 0 }}
                />
              )}
            </div>
          </div>

          {/* Data editor + meta */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium">Dati di esempio (JSON)</p>
                <Button size="sm" variant="ghost" onClick={resetData}>
                  <RefreshCw className="h-3 w-3 mr-1" /> Reset
                </Button>
              </div>
              <Textarea
                value={dataDraft}
                onChange={(e) => setDataDraft(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                spellCheck={false}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Le modifiche vengono applicate automaticamente al render.
              </p>
            </div>
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Trigger</p>
                <p className="font-mono text-sm">{current?.trigger ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Fallback testo</p>
                <Textarea
                  readOnly
                  rows={10}
                  value={renderQ.data?.text ?? ""}
                  className="font-mono text-xs"
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}